import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { TableCompData } from "../../../../components";
import InvestorPortalTableEmpty from "../../../../components/Admin/InvestorPortal/InvestorPortalTableEmpty";
import TableSkeletonLoader from "../../../../components/Common/TableSkeletonLoader";
import { KycStatusBadge, ParticipantTypeBadge } from "../../../../components/Admin/InvestorPortal/AdminStatusBadge";
import InvestorPortalPageHeader from "../../../../components/Admin/InvestorPortal/InvestorPortalPageHeader";
import InvestorPortalTableSection from "../../../../components/Admin/InvestorPortal/InvestorPortalTableSection";
import TableActionDropdown from "../../../../components/Admin/InvestorPortal/TableActionDropdown";
import { useListStaffInvestmentParticipantsQuery } from "../../../../store/admin/investorPortal/staffInvestmentsApi";
import { mapStaffParticipantListItem } from "../../../../utils/staffInvestmentsAdapter";
import { formatMoney } from "../../../../utils/currencyFormat";
import moment from "moment";
import { withInvestorPortalGuard } from "../withInvestorPortalGuard";

const PER_PAGE = 10;

function InvestmentParticipantsPage() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [accountTypeFilter, setAccountTypeFilter] = useState("");
  const [kycFilter, setKycFilter] = useState("");

  const apiQuery = useListStaffInvestmentParticipantsQuery({
    page,
    per_page: PER_PAGE,
    search: search || undefined,
    account_type:
      accountTypeFilter === "cooperative_member"
        ? "member"
        : accountTypeFilter === "standalone_investor"
          ? "investor"
          : undefined,
    kyc_status: kycFilter || undefined,
  });

  const pageData = useMemo(
    () => (apiQuery.data?.items || []).map(mapStaffParticipantListItem).filter(Boolean),
    [apiQuery.data]
  );

  const apiPagination = apiQuery.data?.pagination || {};
  const pagination = {
    currentPage: apiPagination.current_page || page,
    totalPages: apiPagination.last_page || 1,
    total: apiPagination.total || pageData.length,
    perPage: apiPagination.per_page || PER_PAGE,
    onPageChange: setPage,
  };

  const columns = [
    { name: "Name", selector: (p) => p.name, minWidth: "150px", grow: 1 },
    { name: "Type", cell: (p) => <ParticipantTypeBadge type={p.participantType} />, minWidth: "160px" },
    { name: "Opportunities", selector: (p) => p.opportunityCount, minWidth: "115px", center: true },
    { name: "Total committed", selector: (p) => formatMoney(p.totalCommitted), minWidth: "145px", right: true },
    { name: "Approved value", selector: (p) => formatMoney(p.approvedValue), minWidth: "145px", right: true },
    {
      name: "Projected returns",
      selector: (p) => formatMoney(Math.round(p.projectedReturns)),
      minWidth: "150px",
      right: true,
    },
    { name: "Active", selector: (p) => p.activeInvestments, minWidth: "80px", center: true },
    {
      name: "Next maturity",
      selector: (p) => (p.nextMaturity ? moment(p.nextMaturity).format("DD-MMM-YY") : "—"),
      minWidth: "115px",
    },
    { name: "KYC", cell: (p) => <KycStatusBadge status={p.kycStatus} />, minWidth: "100px" },
    {
      name: "Actions",
      cell: (p) => (
        <TableActionDropdown
          items={[
            {
              name: "View requests",
              action: () => {
                const firstRequest = p.requests?.[0];
                if (firstRequest?.id) {
                  navigate(`/super_admin/investor_portal/investments/requests/${firstRequest.id}`);
                } else {
                  navigate("/super_admin/investor_portal/investments/requests");
                }
              },
            },
          ]}
          row={p}
        />
      ),
      width: "80px",
      center: true,
    },
  ];

  const hasFilters = Boolean(search || accountTypeFilter || kycFilter);

  const emptyState = apiQuery.isError ? (
    <InvestorPortalTableEmpty
      variant="error"
      title="We couldn’t load investment participants."
      onCta={() => apiQuery.refetch()}
    />
  ) : hasFilters ? (
    <InvestorPortalTableEmpty
      variant="filtered"
      title="No participants match your filters."
      onCta={() => {
        setSearch("");
        setAccountTypeFilter("");
        setKycFilter("");
        setPage(1);
      }}
    />
  ) : (
    <InvestorPortalTableEmpty
      variant="empty"
      title="No investment participants yet."
      description="Participants appear after investment requests are submitted."
    />
  );

  return (
    <div className="pb-4">
      <InvestorPortalPageHeader
        title="Investment Participants"
        subtitle="All participants with submitted or active investments — standalone investors and cooperative members. For signup-only accounts, see Investor Accounts."
      />

      <div className="row g-2 mb-3">
        <div className="col-md-4">
          <input
            className="form-control investor-portal-note-input"
            placeholder="Search name or email"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
          />
        </div>
        <div className="col-md-3">
          <select
            className="form-select investor-portal-note-input"
            value={accountTypeFilter}
            onChange={(e) => {
              setAccountTypeFilter(e.target.value);
              setPage(1);
            }}
          >
            <option value="">All account types</option>
            <option value="standalone_investor">Standalone investor</option>
            <option value="cooperative_member">Cooperative member</option>
          </select>
        </div>
        <div className="col-md-3">
          <select
            className="form-select investor-portal-note-input"
            value={kycFilter}
            onChange={(e) => {
              setKycFilter(e.target.value);
              setPage(1);
            }}
          >
            <option value="">All KYC statuses</option>
            <option value="approved">Approved</option>
            <option value="pending">Pending</option>
            <option value="under_review">Under review</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>
      </div>

      <InvestorPortalTableSection pagination={pagination}>
        <TableCompData
          columns={columns}
          data={pageData}
          progressPending={apiQuery.isFetching}
          progressComponent={<TableSkeletonLoader rows={8} columns={8} />}
          noDataComponent={emptyState}
        />
      </InvestorPortalTableSection>
    </div>
  );
}

export default withInvestorPortalGuard(InvestmentParticipantsPage, "investment");
